import type { ReactNode } from "react";
import { Link } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Hero } from "@/components/layout/Hero";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { ShareCover, ShareIcon } from "@/components/share/ShareItemCard";
import { ShareUrlButton } from "@/components/share/ShareUrlButton";
import { fmtCount } from "@/lib/share-item";
import { userErrorMessage } from "@/lib/user-error";

const CAROUSELS = [
  { id: "new" as const, icon: "clock" as const, labelId: "share.landing.carouselNew" as const, labelDefault: "Newest" },
  { id: "top" as const, icon: "star" as const, labelId: "share.landing.carouselTop" as const, labelDefault: "Top rated" },
  { id: "interest" as const, icon: "users" as const, labelId: "share.landing.carouselInterest" as const, labelDefault: "Most interested" },
  { id: "discussed" as const, icon: "comment" as const, labelId: "share.landing.carouselDiscussed" as const, labelDefault: "Most comments" },
];
export type CarouselMode = (typeof CAROUSELS)[number]["id"];

interface FeaturedItem {
  id: string; name?: string | null; shortDescription?: string | null; description?: string | null;
  mainImage?: { content?: string | null; mimeType?: string | null } | null;
  views?: number | null; ratings?: number | null; signUps?: number | null; comments?: number | null;
}

export function ShareLandingLayout({ publicView = false, mode, selectMode, list, idx, setIdx, go,
  loading, error, onCreate, onDiscoverAll, children }: {
  publicView?: boolean; mode: CarouselMode; selectMode: (mode: CarouselMode) => void;
  list: readonly FeaturedItem[]; idx: number; setIdx: (index: number) => void; go: (delta: number) => void;
  loading: boolean; error?: unknown; onCreate?: () => void; onDiscoverAll?: () => void; children?: ReactNode;
}) {
  const intl = useIntl();
  const carousels = publicView ? CAROUSELS.filter(c => c.id === "new") : CAROUSELS;
  const activeCarousel = carousels.find(c => c.id === mode);
  const featured = list[idx] ?? list[0];
  const heroLoading = loading && !featured;
  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-48 pt-6 @sm:px-6 @lg:px-10"
      id={publicView ? "public-share" : undefined}
      data-graphql={publicView ? "PublicShareItems" : "SharedItems, MySharedItems"}
    >
      <Hero
        title="SHARE"
        subtitle={
          <>
            <FormattedMessage
              id="share.landing.heroSubtitleLine1"
              defaultMessage="Show what you are working on."
            />
            <br />
            <FormattedMessage
              id="share.landing.heroSubtitleLine2"
              defaultMessage="Find who joins in."
            />
          </>
        }
        body={intl.formatMessage({
          id: "share.landing.heroBody",
          defaultMessage:
            "Publish projects and posts, gather interested people, feedback and reach. And discover what the community is currently building.",
        })}
        image={{ src: "/heroes/share.png" }}
        cta={onCreate ? {
          kind: "button",
          label: intl.formatMessage({ id: "share.landing.heroCta", defaultMessage: "Create ↗" }),
          onClick: onCreate,
          id: "share-create-link",
          anchor: "share.create",
        } : undefined}
      />


      {(list.length > 0 || loading || Boolean(error)) && <>
      <div className="mt-10">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="share.landing.discoverEyebrow" defaultMessage="Discover" />
          </div>
          {onDiscoverAll ? (          <button
            type="button"
            onClick={onDiscoverAll}
            className="text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink"
          >
            <FormattedMessage
              id="share.landing.discoverAllPosts"
              defaultMessage="Discover all posts ↗"
            />
          </button>) : null}
        </div>
        <div id="share-discover-carousel" className="box relative overflow-hidden bg-surface" data-assistant-anchor="share.carousel">
          {/* Fixed desktop height as a FLOOR, not a cap: every text block below has a
              reserved height (min-h + line-clamp), so slides never differ in height,
              but a large title plus a wrapping stats row must be allowed to push the
              box past 400px instead of being clipped away under the action buttons.
              The cover still crops via object-cover. */}
          <div className="grid grid-cols-1 @lg:min-h-[400px] @lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)]">
            {/* Flex column inside the fixed-height box: the action row is
                pinned via mt-auto and the description is the flexible,
                clamped element — when a two-line title eats the budget, copy
                gives way, never the buttons. */}
            <div className="flex min-h-0 flex-col p-5 @sm:p-8 @lg:p-6">
              <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
                <SegmentedControl
                  className="flex-wrap"
                  optionClassName="inline-flex items-center gap-1.5 tracking-[0.15em]"
                  value={mode}
                  onChange={selectMode}
                  options={carousels.map((c) => ({
                    value: c.id,
                    label: (
                      <>
                        <ShareIcon name={c.icon} />{" "}
                        {intl.formatMessage({ id: c.labelId, defaultMessage: c.labelDefault })}
                      </>
                    ),
                  }))}
                />
                {loading ? (
                  <span className="ml-auto text-smycles-micro tracking-[0.2em] text-ink-muted">
                    <FormattedMessage id="share.landing.loading" defaultMessage="loading…" />
                  </span>
                ) : null}
                {error ? (
                  <span role="alert" className="ml-auto text-smycles-micro tracking-[0.2em] text-ink-muted">
                    <FormattedMessage
                      id="share.landing.error"
                      defaultMessage="Error: {message}"
                      values={{ message: userErrorMessage(intl, error) }}
                    />
                  </span>
                ) : null}
              </div>
              {heroLoading ? (
                <HeroSkeleton />
              ) : (
                <>
                {activeCarousel ? (
                  <div className="mb-2 inline-flex shrink-0 items-center gap-1.5 self-start text-smycles-micro tracking-[0.25em] text-ink-accent">
                    <ShareIcon name={activeCarousel.icon} />{" "}
                    {intl.formatMessage({
                      id: activeCarousel.labelId,
                      defaultMessage: activeCarousel.labelDefault,
                    })}
                    {list.length > 0 ? (
                      <span className="text-ink-muted">
                        · {idx + 1}/{list.length}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {/* Leading is 1.25em rather than the tight 0.95em used by unclamped titles
                    elsewhere, and that is a hard constraint, not taste: line-clamp puts its clip
                    edge at exactly N line boxes, so a line-height below the font's own
                    ascent+descent (~1.17em for Helvetica) always cuts the last line's descenders.
                    Buying them back with padding is not an option either, because padding widens the
                    paint area of the very same box, and Blink paints the hidden third line into
                    it, which reads as a sliced-off row under the ellipsis. Height for a
                    single-line title is reserved on the wrapper (2 x 1.25em), never on the
                    clamped element, for the same reason. */}
                <div className="min-h-[2.5em] shrink-0 smycles-page-title leading-[1.25]">
                  <h1 className="line-clamp-2">
                    {featured?.name ?? "–"}
                  </h1>
                </div>
                {/* Title and subtext each reserve a FIXED two-line space
                    (min-h = 2 lines + line-clamp-2), so 1- vs 2-line copy occupies
                    the same height and the stats/buttons below never shift between
                    slides. Longer copy clamps with a trailing "…". */}
                <p id={publicView && !featured && !loading && !error ? "public-share-empty" : undefined} className="mt-3 min-h-[3.25em] shrink-0 line-clamp-2 max-w-xl text-smycles-body leading-relaxed text-ink">
                  {featured
                    ? (publicView ? featured.description : featured.shortDescription) ||
                      intl.formatMessage({
                        id: "share.landing.noShortDescription",
                        defaultMessage: "No short description.",
                      })
                    : loading
                      ? intl.formatMessage({ id: "share.landing.loading", defaultMessage: "loading…" })
                      : intl.formatMessage({
                          id: publicView ? "share.public.empty" : "share.landing.noSharedItems",
                          defaultMessage: "No shared items available.",
                        })}
                </p>
                {!publicView && (
                <div className="mt-4 flex shrink-0 flex-wrap items-end gap-5 @sm:gap-8">
                  <Big
                    n={fmtCount(featured?.views)}
                    l={intl.formatMessage({ id: "share.landing.statViews", defaultMessage: "Views" })}
                  />
                  <Big
                    n={fmtCount(featured?.ratings)}
                    l={intl.formatMessage({
                      id: "share.landing.statRatings",
                      defaultMessage: "Ratings",
                    })}
                  />
                  <Big
                    n={fmtCount(featured?.signUps)}
                    l={intl.formatMessage({
                      id: "share.landing.statInterested",
                      defaultMessage: "Interested",
                    })}
                  />
                  <Big
                    n={fmtCount(featured?.comments)}
                    l={intl.formatMessage({
                      id: "share.landing.statComments",
                      defaultMessage: "Comments",
                    })}
                  />
                </div>
                )}
                <div className="mt-auto flex shrink-0 flex-wrap gap-2 pt-6">
                  <ShareUrlButton
                    path={featured?.id ? `${publicView ? "/share/open-listings" : "/share/discover"}/${featured.id}` : "/share"}
                    label={intl.formatMessage({
                      id: "share.landing.share",
                      defaultMessage: "↗ Share",
                    })}
                    className={buttonClasses({ variant: "secondary", size: "compact" })}
                  />
                  {featured?.id ? (
                    <Link
                      id="share-featured-open"
                      to={`${publicView ? "/share/open-listings" : "/share/discover"}/${featured.id}`}
                      className={buttonClasses({ variant: "primary", size: "compact" })}
                    >
                      <FormattedMessage id="share.landing.open" defaultMessage="Open ↗" />
                    </Link>
                  ) : (
                    <Button
                      variant="primary"
                      size="compact"
                      disabled
                      className="disabled:opacity-40 cursor-not-allowed"
                    >
                      <FormattedMessage id="share.landing.open" defaultMessage="Open ↗" />
                    </Button>
                  )}
                </div>
                </>
              )}
            </div>
            <div className="relative min-h-[260px] @lg:min-h-[300px]">
              <div className="absolute inset-0">
                {heroLoading ? (
                  <div className="h-full w-full smycles-skeleton" aria-hidden="true" />
                ) : (
                  <ShareCover item={featured} index={idx} className="h-full w-full" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {list.length > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => go(-1)}
            aria-label={intl.formatMessage({
              id: "share.landing.previous",
              defaultMessage: "Previous",
            })}
            className="smycles-back-button text-smycles-title leading-none"
          >
            ‹
          </button>
          <div className="flex items-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={intl.formatMessage(
                  { id: "share.landing.slide", defaultMessage: "Slide {number}" },
                  { number: i + 1 },
                )}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-5 bg-accent" : "w-1.5 bg-ink/20 hover:bg-ink/40"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            aria-label={intl.formatMessage({ id: "share.landing.next", defaultMessage: "Next" })}
            className="smycles-back-button text-smycles-title leading-none"
          >
            ›
          </button>
        </div>
      ) : null}

      </>}

      {children}
    </main>
  );
}
// Loading state of the hero's text column. Mirrors the real blocks (label, two-line title,
// two-line copy, four stats, two actions) so the box keeps its height and nothing shifts when the
// data lands. The tabs above stay live, they never depended on the query.
function HeroSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy="true" aria-hidden="true">
      <div className="mb-2 h-2 w-24 shrink-0 smycles-skeleton rounded" />
      <div className="min-h-[2.5em] shrink-0 space-y-2 smycles-page-title">
        <div className="h-[0.7em] w-11/12 smycles-skeleton rounded" />
        <div className="h-[0.7em] w-2/3 smycles-skeleton rounded" />
      </div>
      <div className="mt-3 min-h-[3.25em] shrink-0 max-w-xl space-y-2">
        <div className="h-3 w-full smycles-skeleton rounded" />
        <div className="h-3 w-4/5 smycles-skeleton rounded" />
      </div>
      <div className="mt-4 flex shrink-0 flex-wrap items-end gap-5 @sm:gap-8">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-7 w-8 smycles-skeleton rounded" />
            <div className="h-2 w-14 smycles-skeleton rounded" />
          </div>
        ))}
      </div>
      <div className="mt-auto flex shrink-0 flex-wrap gap-2 pt-6">
        <div className="h-8 w-24 smycles-skeleton rounded-full" />
        <div className="h-8 w-24 smycles-skeleton rounded-full" />
      </div>
    </div>
  );
}

function Big({ n, l, suf }: { n: string; l: string; suf?: string }) {
  return (
    <div>
      <div className="font-serif text-smycles-display-m leading-none">
        {n}
        {suf ? <span className="text-smycles-title text-ink-muted">{suf}</span> : null}
      </div>
      <div className="mt-1 text-smycles-micro tracking-[0.2em] text-ink-muted">{l}</div>
    </div>
  );
}
